import { createContext, type ReactNode, useCallback, useContext, useState } from "react";

import noop from "lodash/noop";

import { setCaseIqSessionId, setZdTicketId } from "../../Support/Components/CaseIQ/utils";
import { useCategoryList, usePlatformList } from "./hooks";
import { type AvaDialogContextType } from "./types";

type Props = {
  children: ReactNode;
};

export const AvaCaseIQContext = createContext<AvaDialogContextType>({
  caseIQConversationId: "",
  setCaseIQConversationId: noop,
  platformList: [],
  categoryList: [],
  isCaseIQOpen: false,
  setIsCaseIQOpen: noop,
  accountManagerEmails: [],
  setAccountManagerEmails: noop,
  billingProfile: "",
  setBillingProfile: noop,
});

export const AvaCaseIQProvider = ({ children }: Props) => {
  const [caseIQConversationId, setCaseIQConversationId] = useState("");
  const [accountManagerEmails, setAccountManagerEmails] = useState<string[]>([]);
  const [isCaseIQOpen, setIsCaseIQOpen] = useState(false);
  const [billingProfile, setBillingProfile] = useState<string>("");

  const platformList = usePlatformList();

  const categoryList = useCategoryList();

  const handleCaseIQConversationIdChange = useCallback(async (caseIQConversationId?: string) => {
    setCaseIqSessionId(crypto.randomUUID());
    setZdTicketId("");
    setIsCaseIQOpen(true);
    setBillingProfile("");
    if (!caseIQConversationId) {
      setCaseIQConversationId("");
      return;
    }

    setCaseIQConversationId(caseIQConversationId);
  }, []);

  return (
    <AvaCaseIQContext.Provider
      value={{
        caseIQConversationId,
        setCaseIQConversationId: handleCaseIQConversationIdChange,
        platformList,
        categoryList,
        isCaseIQOpen,
        setIsCaseIQOpen,
        accountManagerEmails,
        setAccountManagerEmails,
        billingProfile,
        setBillingProfile,
      }}
    >
      {children}
    </AvaCaseIQContext.Provider>
  );
};

export const useAvaCaseIQContext = () => useContext(AvaCaseIQContext);
